import React, {useCallback} from 'react';
import {
    POSITION_TYPES,
    useShelves,
    useShelvesInit,
} from 'hooks/Product/Shelves';
import {Button, Col, Row} from 'react-bootstrap';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {CBCButton, getFieldValue, Icon} from 'shared/helpers';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {useProductContext} from 'contexts/ProductContext';
import {GenericFields} from 'components/customer/Product/Specs/GenericFields';
import {useFormikContext} from 'formik';
import {AddShelfDialog} from 'components/customer/Product/Specs/AddShelfDialog';
import {Field} from 'components/customer/Product/entity/Field';
import {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {ConvertButton} from 'components/customer/Product/Specs/ConvertButton';
import {cloneDeep} from 'lodash';
import styled from 'styled-components';

interface Shelf {
    shelf_offset: number;
    shelf_position: number;
    shelf_style: number;
    shelf_type: number;
}

interface UpperShelf {
    upper_shelf_offset: number;
    upper_shelf_position: number;
    upper_shelf_style: number;
    upper_shelf_type: number;
}

interface LowerShelf {
    lower_shelf_offset: number;
    lower_shelf_position: number;
    lower_shelf_style: number;
    lower_shelf_type: number;
}

interface MiddleShelf {
    middle_shelf_offset: number;
    middle_shelf_position: number;
    middle_shelf_style: number;
    middle_shelf_type: number;
}

type ShelfKey =
    keyof Shelf | keyof UpperShelf | keyof LowerShelf | keyof MiddleShelf;

export const Shelves = ({fieldset}: {fields?: Field[]; fieldset: Fieldset}) => {
    const useShelvesResult = useShelves(fieldset);
    const fieldsetName = useShelvesResult.fieldsetName as string;
    const {
        addShelfDialog,
        deleteShelfDialog,
        copyShelfDialog,
        autoPosition,
        dialog,
        shelfFieldsets,
        isLimitReached,
    } = useShelvesResult;
    const {setPositionType} = useProductContext();
    const {values, setFieldValue, setFieldTouched} = useFormikContext<{
        [key: string]: Shelf[] | UpperShelf[] | LowerShelf[] | MiddleShelf[];
    }>();
    const isSmallDevice = isDeviceSmall();

    useShelvesInit(fieldsetName);

    const togglePositionType = useCallback(
        (name: string, value: string) => () => {
            if (setPositionType) {
                setPositionType(name, value);
            }
        },
        []
    );

    const copyShelfDialogHandler = useCallback(
        (index: number) => () => {
            copyShelfDialog(index);
        },
        [copyShelfDialog]
    );

    const deleteShelfDialogHandler = useCallback(
        (index: number) => () => {
            deleteShelfDialog(index);
        },
        [deleteShelfDialog]
    );

    const addShelfDialogHandler = useCallback(
        () => addShelfDialog(<AddShelfDialog fieldsetName={fieldsetName} />),
        [addShelfDialog]
    );

    const selectHandler = useCallback(
        (index: number) => (name: ShelfKey, value: number) => {
            const shelves = cloneDeep(values[String(fieldsetName)]);

            // eslint-disable-next-line security/detect-object-injection -- Using a type-safe record accessor pattern
            shelves[Number(index)][name] = value;

            void setFieldValue(fieldsetName, shelves);
            void setFieldTouched(fieldsetName, true);
        },
        [fieldsetName, values, setFieldValue]
    );

    return (
        <>
            {shelfFieldsets.length
                ? shelfFieldsets.map((shelfFieldset: Fieldset, index) => {
                      const indexedValue = values[String(fieldsetName)]
                          ? values[String(fieldsetName)][Number(index)]
                          : {};
                      const {id = index}: {id?: number} = indexedValue || {};

                      return (
                          <React.Fragment key={id}>
                              <Row>
                                  <Col className="shelf" md={12}>
                                      <section
                                          style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                          }}>
                                          <strong>
                                              {getFieldValue(fieldset.title, {
                                                  fieldSet: {index: index},
                                              })}
                                          </strong>
                                          <div className="shelf-actions">
                                              <OverlayTrigger
                                                  placement={
                                                      isSmallDevice
                                                          ? 'top'
                                                          : 'right'
                                                  }
                                                  overlay="Copy Shelf">
                                                  <Button
                                                      disabled={isLimitReached}
                                                      tabIndex={-1}
                                                      variant="link"
                                                      onClick={copyShelfDialogHandler(
                                                          index
                                                      )}>
                                                      <Icon iconName="Options-Copy.svg" />
                                                  </Button>
                                              </OverlayTrigger>
                                              <OverlayTrigger
                                                  placement={
                                                      isSmallDevice
                                                          ? 'top'
                                                          : 'right'
                                                  }
                                                  overlay="Delete Shelf">
                                                  <Button
                                                      tabIndex={-1}
                                                      variant="link"
                                                      onClick={deleteShelfDialogHandler(
                                                          index
                                                      )}>
                                                      <Icon iconName="Options-Delete.svg" />
                                                  </Button>
                                              </OverlayTrigger>
                                          </div>
                                      </section>
                                  </Col>
                              </Row>
                              <Row>
                                  <Col className="shelf-fields" md={12}>
                                      <GenericFields
                                          fields={shelfFieldset.fields}
                                          fieldsetName={shelfFieldset.name}
                                          fieldsetTitle={shelfFieldset.title}
                                          fieldSetIndex={index}
                                          indexedValues={indexedValue}
                                          selectHandler={selectHandler(index)}
                                      />
                                  </Col>
                              </Row>

                              {index <= shelfFieldsets.length - 2 ? (
                                  <hr />
                              ) : null}
                          </React.Fragment>
                      );
                  })
                : null}
            <ButtonsContainer>
                <CBCButton
                    tabIndex={-1}
                    iconName="Button-Add-Shelf.svg"
                    disabled={isLimitReached}
                    onClick={addShelfDialogHandler}>
                    Add a Shelf
                </CBCButton>
                {values[String(fieldsetName)]?.length > 0 ? (
                    <>
                        <CBCButton
                            tabIndex={-1}
                            iconName="Button-Auto-Positions.svg"
                            onClick={autoPosition}>
                            Auto Positions
                        </CBCButton>
                        <ConvertButton
                            fieldsetName={fieldsetName}
                            handleConvertToPercentage={togglePositionType(
                                fieldsetName,
                                POSITION_TYPES.PERCENTAGE
                            )}
                            handleConvertToMM={togglePositionType(
                                fieldsetName,
                                POSITION_TYPES.DECIMAL
                            )}
                        />
                    </>
                ) : null}
            </ButtonsContainer>

            {dialog}
        </>
    );
};

const ButtonsContainer = styled.div`
    display: flex;
    gap: 10px;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: 15px;

    > button {
        font-size: 0.8em;
        padding: 2px 7px 2px 2px;
        flex-grow: 1;
        min-width: 140px;
    }
`;
